<script setup>
definePage({
  meta: {
    layout: "blank",
    unauthenticatedOnly: false,
  },
})
import { langTypes } from "@/utils/constant"
import StudentService from "@/services/StudentService"
import QuestionWrap from "@/components/question/Wrap.vue"
import ContentError from "@/components/common/ContentError.vue"
import BtnLang from "@/components/BtnLang.vue"
import { useCustomSwal } from "@/composables/usePopup"
import { useSurveyStore } from "@/stores/surveyStore"
const { showCustomSwal } = useCustomSwal()

const campaign = ref(import.meta.env.VITE_CAMPAIGN == 1)

const block1 = [
  {
    id: "",
    type: "alert",
    content: `
    <div>${$gettext(
      "Attention, si vous passez plus de 30 minutes sur cette page ou que vous laissez cet onglet ouvert trop longtemps, votre session va expirer et vos réponses ne seront pas enregistrées."
    )}</div>
    <div>${$gettext(
      "Si vous ne recevez pas de mail de confirmation après avoir validé votre formulaire, c'est que vos réponses n'ont pas été prises en compte."
    )}</div>
    <div>${$gettext(
      "N'hésitez pas à nous contacter en cas de doute ou de difficulté par mail :"
    )} <a href="mailto:classements.france@eduniversal-group.com"><u>classements.france@eduniversal-group.com</u></a> </div>
    `,
  },
]

const block2 = [
  {
    id: "",
    type: "info",
    content: `<div>
                ${$gettext(
                  "Si vous n'avez pas suivi ce programme : ne répondez pas au formulaire et contactez-nous au plus vite en nous envoyant un email sur :"
                )} <a href="mailto:classements.france@eduniversal-group.com"><u>classements.france@eduniversal-group.com</u></a> 
                ${$gettext("ou un sms / whatsapp au ")}
                <a href="tel:0630231266"><u>06 30 23 12 66</u></a> / <a href="tel:0644154471"><u>06 44 15 44 71</u></a>
            </div>`,
  },
]

const router = useRouter()
const route = useRoute()
const { params } = route
const refVForm = ref()
const isConfirmDialogVisible = ref(false)
const successNoti = ref(false)
const errorNoti = ref(false)
const messageNoti = ref("")
const errorData = ref(null)

const info = ref({})
const selectedAnswers = ref({})
const questions = ref([])
const loadingSubmit = ref(false)
const isPublishSA = ref(null)
const nickName = ref(null)
const answered = ref(false)
const isAdmin = computed(() => route.query.isAdmin)

// support check error
function getAllSiblings(element) {
  let result = [],
    node = element.parentNode.firstChild

  while (node) {
    if (node !== element && node.nodeType === Node.ELEMENT_NODE)
      result.push(node)
    node = node.nextElementSibling || node.nextSibling
  }
  return result
}
function findQuestionTitle(element) {
  const found = getAllSiblings(element).find((e) => {
    return [...e.classList].includes("question-title")
  })
  if (found) return found
  if (element.parentNode.tagName.toLowerCase() === "body") return null
  return found ?? findQuestionTitle(element.parentNode)
}
function scrollToElement(element) {
  if (!element) return

  const yOffset = -200
  const y = element.getBoundingClientRect().top + window.scrollY + yOffset

  window.scrollTo({ top: y, behavior: "smooth" })
}
// end support check error

const randomText = () => {
  const chars = "abcdefghijklmnopqrstuvwxyz"
  let text = Array.from(
    { length: 8 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("")
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const onChangeLang = (lang) => {
  const locale = langTypes.find((x) => x.id === lang)
  if (locale) {
    const urlObj = new URL(window.location.href)
    urlObj.searchParams.set("lang", locale.name) // Replace 'en' with your desired language
    const newUrl = urlObj.toString()
    window.location.href = newUrl
  }
}

const onSubmit = async () => {
  try {
    const { valid: isValid, errors } = await refVForm.value?.validate()
    if (isValid) {
      loadingSubmit.value = true
      // await showCustomSwal({
      //   icon: "success",
      // });
      const response = await StudentService.updateSurvey({
        id: info.value?.id,
        answers: selectedAnswers.value,
        isPublishSA: isPublishSA.value,
        nickName: nickName.value,
        hash: params.id,
      })

      if (response) {
        await showCustomSwal({
          html: `
          <p style="font-size: 14px;" class="mb-0">
            <b>${$gettext("Merci pour votre participation !")}</b> <br/>
            ${$gettext("Votre réponse a été prise en compte.")} <br/>
            ${$gettext(
              "Si vous souhaitez publier vos avis, ne fermez pas cette page avant que la page de StudiesAdvisor ne s'ouvre !"
            )}
          </p>
          `,
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
          timerProgressBar: true,
          timer: 10000,
          width: 490,
          position: "center",
          willClose: () => {
            useCookie("lang").value = null
            if (response.redirectUrl)
              window.location.href = response.redirectUrl
            else router.push("/")
          },
        })
        answered.value = true
      } else {
        await showCustomSwal({
          html: `<div class="font-weight-regular">${error.value}</div>`,
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
          timerProgressBar: true,
          timer: 10000,
          width: 490,
          position: "center",
        })
        messageNoti.value = error.value
      }

      loadingSubmit.value = false
    } else {
      const firstError = errors[0]
      const inputElement = document.getElementById(firstError.id + "-messages")
      const titleElement = findQuestionTitle(inputElement)

      if (titleElement) {
        await showCustomSwal({
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
          timerProgressBar: true,
          timer: 10000,
          width: 490,
          position: "center",
          html: `<p>${$gettext("La question")}: <b>${
            titleElement.innerHTML
          }</b> ${firstError.errorMessages[0]} </p>`,
        })
        setTimeout(() => {
          scrollToElement(titleElement)
        }, 300)
      }

      return
    }
  } catch (error) {
    errorData.value = error?.data
  }
}

watch(
  () => isPublishSA.value,
  (val) => {
    if (val && !nickName.value) {
      nickName.value = randomText() + "_" + info.value?.id
    }
  }
)

onMounted(async () => {
  if (!campaign.value && !isAdmin.value) {
    router.push("/")
    return
  }
  try {
    const res = await StudentService.getSurvey(params.id)
    useSurveyStore().setSurvey(res)
    const steps = res?.steps || []
    questions.value = steps[0]?.questions
    info.value = {
      id: res?.id,
      formType: res?.formType,
      program: res?.program,
    }

    nextTick(() => {
      selectedAnswers.value = { ...res?.answers }
      if (res?.isPublishSA !== null) {
        isPublishSA.value = res?.isPublishSA ? 1 : 0
      }
      nickName.value = res?.surveyor?.contact?.nickName || null
      answered.value = res?.isValidated ? true : false
    })
  } catch (error) {
    errorData.value = error?.data
  }
})
</script>
<template>
  <div class="register-student">
    <div class="register-student__container">
      <div class="register-student__bg">
        <img src="@/assets/images/palm_bg.svg" alt="palm" style="top: 0" />
      </div>
      <!-- Start Header -->
      <header
        class="d-flex align-center justify-center register-student__container-header"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img
            src="@/assets/logo-eduniversal.png"
            alt="Logo"
            style="max-width: 140px"
          />
        </RouterLink>
        <h1 class="title">
          {{ $gettext("Enquêtes") }} France {{ CURRENT_YEAR }}
        </h1>

        <BtnLang @onChange="onChangeLang" />
      </header>
      <!-- End Header -->
      <main class="register-student__main" style="background: #fff">
        <ContentError v-if="errorData?.code" :code="errorData?.code" />
        <template v-else>
          <!-- Start Top -->
          <div class="form__title" style="max-width: none">
            <h3 class="mt-4" v-if="info?.program?.type === 2">
              {{ $gettext("Enquêtes") }} {{ CURRENT_YEAR }}
              {{ $gettext("des") }} Meilleurs Licences, Bachelors
              {{ $gettext("et") }}
              Grandes Écoles
            </h3>
            <h3 class="mt-4" v-else>
              {{ $gettext("Enquêtes") }} {{ CURRENT_YEAR }}
              {{ $gettext("des") }} Meilleurs Masters, MS
              {{ $gettext("et") }} MBA
            </h3>

            <InfoBlockList :infoBlocks="block1" class="text-left" />

            <h4>
              {{ $gettext("Votre formulaire concerne :") }}
            </h4>
            <h3>
              {{ info.program?.school?.name }}
            </h3>
            <h3>
              {{ info.program?.name }}
            </h3>

            <InfoBlockList :infoBlocks="block2" class="text-left" />
          </div>
          <!-- End Top -->
          <div class="py-6">
            <VForm
              ref="refVForm"
              :style="{
                'pointer-events': answered && !isAdmin ? 'none' : 'all',
              }"
            >
              <question-wrap
                v-for="question in questions"
                :key="question.id"
                :question="question"
                v-model:selectedAnswers="selectedAnswers"
              />

              <div class="px-4">
                <img
                  src="@/assets/images/logoSA.png"
                  alt="Logo"
                  width="400"
                  class="student-image"
                  style="
                    display: block;
                    margin-bottom: -2rem;
                    margin-left: -1rem;
                  "
                />
                <div
                  class="text-uppercase font-weight-bold text-h6 mb-3"
                  style="color: #2a81ba"
                >
                  {{
                    $gettext(
                      "Publiez anonymement vos commentaires et aidez les futures générations d'étudiants !"
                    )
                  }}
                </div>
                <div>
                  {{
                    $gettext(
                      "Parce qu'il est important que les étudiants aient tous une information éclairée sur leur poursuite d'études, Eduniversal et son partenaire StudiesAdvisor.net unissent leurs forces et vous permettent aujourd'hui, en avant première, de publier, anonymement et en quelques clics les commentaires que vous venez de nous transmettre dans le cadre de l'enquête relative au Classement Eduniversal."
                    )
                  }}
                </div>

                <v-radio-group
                  v-model="isPublishSA"
                  :rules="[requiredNullOrUndefined]"
                >
                  <v-radio :value="1">
                    <template v-slot:label>
                      <div class="white-space-normal">
                        {{
                          $gettext("Je souhaite publier mes commentaires sur")
                        }}
                        <b>
                          <a href="https://studiesadvisor.net/" target="_blank">
                            &nbsp;StudiesAdvisor.net
                          </a>
                        </b>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio :value="0">
                    <template v-slot:label>
                      <div class="white-space-normal">
                        {{
                          $gettext(
                            "Je ne souhaite pas publier mes commentaires sur"
                          )
                        }}
                        <b>
                          <a href="https://studiesadvisor.net/" target="_blank">
                            &nbsp;StudiesAdvisor.net
                          </a>
                        </b>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
                <div class="mt-2" v-if="isPublishSA === 1">
                  <div class="mb-2">
                    {{
                      $gettext(
                        "Signer vos commentaires (modification possible)"
                      )
                    }}
                  </div>
                  <v-text-field
                    v-model="nickName"
                    :rules="[requiredValidator]"
                  ></v-text-field>
                </div>
              </div>

              <div class="text-center">
                <SubmitBtn
                  class="mt-8"
                  @click="onSubmit"
                  :loading="loadingSubmit"
                  :disabled="answered && !isAdmin"
                >
                  {{ $gettext("Valider mon formulaire") }}
                </SubmitBtn>
              </div>
            </VForm>
          </div>
        </template>
      </main>
    </div>
  </div>

  <Notification
    v-model:isDialogVisible="isConfirmDialogVisible"
    :success="successNoti"
    :error="errorNoti"
    :message="messageNoti"
  />
  <VCard style="margin: 30px 16px 50px 16px">
    <ContactWithUs />
  </VCard>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";
.student-image {
  @media (max-width: 998px) {
    width: 100%;
    margin-bottom: 0 !important;
  }
}
.white-space-normal {
  @media (max-width: 998px) {
    white-space: normal !important;
  }
}
</style>
